import { db } from "../../main";

import Report, { ReportType, ReportUsage } from "../models/Report";
import { getClientRef } from "./ClientRepository";
import { filterOutUndefined } from "@/utils/Utils";

function getReportsCollectionRef() {
  const clientRef = getClientRef();
  const ref = `${clientRef}/reports`;

  return db.collection(ref);
}

function getReportRef(report: Report) {
  const clientRef = getClientRef();

  const ref = `${clientRef}/reports/${report.id}`;

  return db.doc(ref);
}

export async function saveReport(report: Report) {
  return getReportRef(report).set(filterOutUndefined(report), { merge: true });
}

function getReportsOfProjectQuery(projectId: string) {
  return getReportsCollectionRef()
    .where('projectId', '==', projectId)
    .where('usage', '==', ReportUsage.INTERNAL);
}

async function getReportsNotOutdated(projectId: string): Promise<Report[]> {
  const query = getReportsOfProjectQuery(projectId).where('outdated', '==', false);
  const snapshot = await query.get();

  return snapshot.docs.map((doc) => doc.data() as Report);
}

async function setReportsOutdated(
  projectId: string,
  callback: (report: Report) => boolean
): Promise<void> {
  const reports = await getReportsNotOutdated(projectId);
  const outdatedReports = reports.filter(callback);

  // Update all the reports marked as outdated
  const updatePromises = outdatedReports.map((report) =>
    saveReport({ ...report, outdated: true })
  );

  // Wait for all updates to finish
  await Promise.all(updatePromises);
}

export async function deleteReport(report: Report) {
  return getReportRef(report).delete();
}

export function observeReportsOfProject(
  projectId: string,
  onNext: (reports: Array<Report>) => void
): () => void {
  const clientRef = getClientRef();

  return db
    .collection(clientRef + "/reports")
    .where("projectId", "==", projectId)
    .orderBy("reportDate", "desc")
    .onSnapshot((snapshot) => {
      const result: Array<Report> = [];
      snapshot.forEach((doc) => {
        const report = doc.data() as Report;
        if (report) {
          result.push(report);
        }
      });

      onNext(result);
    });
}

export async function setProjectReportsOutdated(projectId: string): Promise<void> {
  await setReportsOutdated(projectId, (report: Report) => {
    return report.reportType === ReportType.INTERIM_REPORT || report.reportType === ReportType.FINAL_REPORT;
  });
}

export async function setLeakDetectionReportsOutdated(projectId: string): Promise<void> {
  await setReportsOutdated(projectId, (report: Report) => {
    return report.reportType === ReportType.LEAK_DETECTION_REPORT;
  });
}

export async function setDamageReportsOutdated(projectId: string): Promise<void> {
  await setReportsOutdated(projectId, (report: Report) => {
    return report.reportType === ReportType.DAMAGE_REPORT;
  });
}